import { forwardRef, memo, useId } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../../lib/styles';
import { Label } from '../Label';

export const textareaStyles = tv({
  base: 'flex px-2.5 py-2 font-body-sm font-medium resize-none w-full field-sizing-content transition-all enabled:placeholder:text-text-tertiary enabled:text-text-primary disabled:text-text-disabled',
  variants: {
    variant: {
      default:
        'bg-bg-inter-secondary-default outline-1 outline-bg-inter-secondary-default focus-visible:outline-border-input-select rounded-sm',
      unstyled: 'bg-transparent border-none outline-none',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface TextareaProps extends React.ComponentProps<'textarea'>, VariantProps<typeof textareaStyles> {
  label?: string;
  textareaClassName?: string;
  moveCaretToEndOnFocus?: boolean;
  labelClassName?: string;
}

const TextareaBase = (
  {
    className,
    label,
    id,
    textareaClassName,
    variant,
    moveCaretToEndOnFocus = true,
    onFocus,
    labelClassName,
    ...props
  }: TextareaProps,
  ref: React.ForwardedRef<HTMLTextAreaElement>,
) => {
  const generatedId = useId();
  // A stable id keeps the visible <Label htmlFor> associated with the textarea.
  const fieldId = id || generatedId;
  // Without a visible <Label>, fall back to the placeholder for an accessible name
  // (a caller-provided aria-label in `props` still wins where it is spread below).
  const fallbackAriaLabel = !label && typeof props.placeholder === 'string' ? props.placeholder : undefined;

  const handleFocus: React.FocusEventHandler<HTMLTextAreaElement> = (e) => {
    if (moveCaretToEndOnFocus) {
      const length = e.currentTarget.value.length;
      e.currentTarget.setSelectionRange(length, length);
    }
    onFocus?.(e);
  };

  const textareaInput = (
    <textarea
      ref={ref}
      id={fieldId}
      data-slot="textarea"
      className={cn(textareaStyles({ variant }), label ? textareaClassName : className)}
      onFocus={handleFocus}
      aria-label={fallbackAriaLabel}
      {...props}
    />
  );

  if (!label) return textareaInput;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Label htmlFor={fieldId} className={labelClassName}>
        {label}
      </Label>

      {textareaInput}
    </div>
  );
};

export const Textarea = memo(forwardRef(TextareaBase));
